import { useState } from 'react';
import { MessageCircle, Send, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '@/contexts/LanguageContext';
import { useSettings } from '@/contexts/SettingsContext';
import SEO from '@/components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const { t, language } = useLanguage();
  const { getWhatsAppUrl } = useSettings();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    experience_type: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const cityOptions = {
    en: [
      { value: '', label: 'Where are you drawn to?' },
      { value: 'sao-paulo', label: 'São Paulo' },
      { value: 'rio-de-janeiro', label: 'Rio de Janeiro' },
      { value: 'both', label: 'Both Cities' }
    ],
    pt: [
      { value: '', label: 'Para onde você quer ir?' },
      { value: 'sao-paulo', label: 'São Paulo' },
      { value: 'rio-de-janeiro', label: 'Rio de Janeiro' },
      { value: 'both', label: 'Ambas' }
    ],
    es: [
      { value: '', label: '¿A dónde quieres ir?' },
      { value: 'sao-paulo', label: 'São Paulo' },
      { value: 'rio-de-janeiro', label: 'Río de Janeiro' },
      { value: 'both', label: 'Ambas' }
    ],
    fr: [
      { value: '', label: 'Où souhaitez-vous aller?' },
      { value: 'sao-paulo', label: 'São Paulo' },
      { value: 'rio-de-janeiro', label: 'Rio de Janeiro' },
      { value: 'both', label: 'Les Deux' }
    ]
  };

  const experienceOptions = {
    en: [
      { value: '', label: 'What brings you here?' },
      { value: 'nightlife', label: 'Nightlife & Social' },
      { value: 'private', label: 'Private Experiences' },
      { value: 'events', label: 'Events & Celebrations' },
      { value: 'custom', label: 'Something Specific' }
    ],
    pt: [
      { value: '', label: 'O que o traz aqui?' },
      { value: 'nightlife', label: 'Vida Noturna & Social' },
      { value: 'private', label: 'Experiências Privadas' },
      { value: 'events', label: 'Eventos & Celebrações' },
      { value: 'custom', label: 'Algo Específico' }
    ],
    es: [
      { value: '', label: '¿Qué te trae aquí?' },
      { value: 'nightlife', label: 'Vida Nocturna & Social' },
      { value: 'private', label: 'Experiencias Privadas' },
      { value: 'events', label: 'Eventos & Celebraciones' },
      { value: 'custom', label: 'Algo Específico' }
    ],
    fr: [
      { value: '', label: 'Qu\'est-ce qui vous amène ici?' },
      { value: 'nightlife', label: 'Vie Nocturne & Social' },
      { value: 'private', label: 'Expériences Privées' },
      { value: 'events', label: 'Événements & Célébrations' },
      { value: 'custom', label: 'Quelque Chose de Spécifique' }
    ]
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.data.success) {
        setIsSubmitted(true);
        toast.success(t('contact.success.message'));
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const whatsappMessages = {
    en: 'Hello, I\'d like to learn more about Velmont Society.',
    pt: 'Olá, gostaria de saber mais sobre a Velmont Society.',
    es: 'Hola, me gustaría saber más sobre Velmont Society.',
    fr: 'Bonjour, je voudrais en savoir plus sur Velmont Society.'
  };
  
  const whatsappUrl = getWhatsAppUrl(whatsappMessages[language] || whatsappMessages.en);

  if (isSubmitted) {
    return (
      <div data-testid="contact-success" className="min-h-screen pt-24 md:pt-32 flex items-center justify-center px-6">
        <SEO 
          title={t('contact.tagline')}
          description={t('contact.description')}
        />
        <div className="max-w-lg text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 border border-gold/40 mb-10">
            <CheckCircle className="text-gold/80" size={36} strokeWidth={1} />
          </div>
          <h1 className="font-playfair text-3xl md:text-4xl text-[#F5F5F0] mb-6">
            {t('contact.success.title')} <span className="italic">{t('contact.success.highlight')}</span>
          </h1>
          <p className="text-[#A1A1AA] text-base font-manrope font-light mb-4 leading-relaxed">
            {t('contact.success.message')}
          </p>
          <p className="text-[#52525B] text-sm font-manrope font-light mb-10">
            {t('contact.success.note')}
          </p>
          <a
            href="/"
            data-testid="return-home-link"
            className="inline-flex items-center gap-3 text-gold text-xs uppercase tracking-[0.2em] font-medium hover:text-gold-light transition-colors underline-hover"
          >
            {t('contact.success.home')}
          </a>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="contact-page" className="min-h-screen pt-24 md:pt-32">
      <SEO 
        title={t('contact.tagline')}
        description={t('contact.description')}
      />
      
      {/* Page Header */}
      <section className="px-6 md:px-12 lg:px-24 py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto">
          <div className="max-w-2xl">
            <p className="text-gold text-xs uppercase tracking-[0.4em] mb-5 font-manrope font-medium">
              {t('contact.tagline')}
            </p>
            <h1 className="font-playfair text-4xl md:text-5xl lg:text-6xl text-[#F5F5F0] mb-8 leading-[1.1]">
              {t('contact.title')} <span className="italic">{t('contact.title.highlight')}</span>
            </h1>
            <p className="text-[#A1A1AA] text-base md:text-lg font-manrope font-light leading-relaxed">
              {t('contact.description')}
            </p>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="px-6 md:px-12 lg:px-24 pb-28 md:pb-36">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
            {/* Contact Form */}
            <div>
              <form onSubmit={handleSubmit} className="space-y-10">
                {/* Name */}
                <div>
                  <label 
                    htmlFor="name" 
                    className="text-xs uppercase tracking-[0.2em] text-[#52525B] mb-4 block font-manrope"
                  >
                    {t('contact.name')}
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    data-testid="contact-name-input"
                    className="w-full bg-transparent border-b border-[#27272A] focus:border-gold px-0 py-4 text-[#F5F5F0] placeholder:text-[#3F3F46] focus:outline-none transition-colors duration-500 font-manrope"
                    placeholder={t('contact.name.placeholder')}
                  />
                </div>

                {/* Email */}
                <div>
                  <label 
                    htmlFor="email" 
                    className="text-xs uppercase tracking-[0.2em] text-[#52525B] mb-4 block font-manrope"
                  >
                    {t('contact.email')}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    data-testid="contact-email-input"
                    className="w-full bg-transparent border-b border-[#27272A] focus:border-gold px-0 py-4 text-[#F5F5F0] placeholder:text-[#3F3F46] focus:outline-none transition-colors duration-500 font-manrope"
                    placeholder="your@email.com"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label 
                    htmlFor="phone" 
                    className="text-xs uppercase tracking-[0.2em] text-[#52525B] mb-4 block font-manrope"
                  >
                    {t('contact.phone')}
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    data-testid="contact-phone-input"
                    className="w-full bg-transparent border-b border-[#27272A] focus:border-gold px-0 py-4 text-[#F5F5F0] placeholder:text-[#3F3F46] focus:outline-none transition-colors duration-500 font-manrope"
                    placeholder={t('contact.phone.placeholder')}
                  />
                </div>

                {/* City */}
                <div>
                  <label 
                    htmlFor="city" 
                    className="text-xs uppercase tracking-[0.2em] text-[#52525B] mb-4 block font-manrope"
                  >
                    {t('contact.city')}
                  </label>
                  <select
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    data-testid="contact-city-select"
                    className="w-full bg-transparent border-b border-[#27272A] focus:border-gold px-0 py-4 text-[#F5F5F0] focus:outline-none transition-colors duration-500 font-manrope appearance-none cursor-pointer"
                  >
                    {(cityOptions[language] || cityOptions.en).map(option => (
                      <option 
                        key={option.value} 
                        value={option.value}
                        className="bg-obsidian text-[#F5F5F0]"
                      >
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Experience Type */}
                <div>
                  <label 
                    htmlFor="experience_type" 
                    className="text-xs uppercase tracking-[0.2em] text-[#52525B] mb-4 block font-manrope"
                  >
                    {t('contact.experience')}
                  </label>
                  <select
                    id="experience_type"
                    name="experience_type"
                    value={formData.experience_type}
                    onChange={handleChange}
                    data-testid="contact-experience-select"
                    className="w-full bg-transparent border-b border-[#27272A] focus:border-gold px-0 py-4 text-[#F5F5F0] focus:outline-none transition-colors duration-500 font-manrope appearance-none cursor-pointer"
                  >
                    {(experienceOptions[language] || experienceOptions.en).map(option => (
                      <option 
                        key={option.value} 
                        value={option.value}
                        className="bg-obsidian text-[#F5F5F0]"
                      >
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Message */}
                <div>
                  <label 
                    htmlFor="message" 
                    className="text-xs uppercase tracking-[0.2em] text-[#52525B] mb-4 block font-manrope"
                  >
                    {t('contact.message')}
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    value={formData.message}
                    onChange={handleChange}
                    data-testid="contact-message-textarea"
                    className="w-full bg-transparent border-b border-[#27272A] focus:border-gold px-0 py-4 text-[#F5F5F0] placeholder:text-[#3F3F46] focus:outline-none transition-colors duration-500 font-manrope resize-none"
                    placeholder={t('contact.message.placeholder')}
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="contact-submit-button"
                  className="w-full md:w-auto bg-gold text-black px-12 py-4 text-xs uppercase tracking-[0.2em] font-bold hover:bg-gold-light transition-all duration-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 luxury-btn mt-4"
                >
                  {isSubmitting ? (
                    <>Processing...</>
                  ) : (
                    <>
                      {t('contact.submit')}
                      <Send size={14} />
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Side Info */}
            <div className="flex flex-col justify-center">
              <div className="bg-obsidian-paper border border-white/5 p-10 md:p-12 mb-10">
                <p className="text-gold text-xs uppercase tracking-[0.4em] mb-5 font-manrope font-medium">
                  {t('contact.whatsapp.title')}
                </p>
                <h3 className="font-playfair text-2xl text-[#F5F5F0] mb-5">
                  {t('contact.whatsapp.subtitle')}
                </h3>
                <p className="text-[#A1A1AA] text-sm font-manrope font-light mb-8 leading-relaxed">
                  {t('contact.whatsapp.description')}
                </p>
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp-button"
                  className="inline-flex items-center gap-3 border border-gold text-gold px-8 py-3 text-xs uppercase tracking-[0.2em] font-bold hover:bg-gold hover:text-black transition-all duration-500"
                >
                  <MessageCircle size={16} />
                  {t('contact.whatsapp.button')}
                </a>
              </div>

              {/* Additional Info */}
              <div className="space-y-8">
                <div>
                  <h4 className="text-gold text-xs uppercase tracking-[0.2em] mb-3 font-manrope font-medium">
                    {t('contact.next')}
                  </h4>
                  <p className="text-[#A1A1AA] text-sm font-manrope font-light leading-relaxed">
                    {t('contact.next.desc')}
                  </p>
                </div>
                <div>
                  <h4 className="text-gold text-xs uppercase tracking-[0.2em] mb-3 font-manrope font-medium">
                    {t('contact.discretion')}
                  </h4>
                  <p className="text-[#A1A1AA] text-sm font-manrope font-light leading-relaxed">
                    {t('contact.discretion.desc')}
                  </p>
                </div>
                <div>
                  <h4 className="text-gold text-xs uppercase tracking-[0.2em] mb-3 font-manrope font-medium">
                    {t('contact.obligation')}
                  </h4>
                  <p className="text-[#A1A1AA] text-sm font-manrope font-light leading-relaxed">
                    {t('contact.obligation.desc')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
